import { useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useAppDispatch, shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import uuidV4 from 'lib/string/uuidV4Utils'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { transformUserCartItemsToCheckoutItems } from 'lib/checkout/userCart'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { getOffersById } from 'api/offer'
import { reportLog } from 'services/userMonitoring'
import { fetchCharterBundleFlightItems, resolveCharterBundle, type CharterBundleHydrationResult, type CharterFlightBundleSelection } from 'tripPlanner/utils/charterBundleHydration'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { useShouldWaiveLuxPlusJoiningFee } from 'hooks/UserCart/useCartItems'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { isExperienceItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import { getMultiCityGroupId, getMultiCityLegOrder } from '../utils/multiCityFlightHelpers'
import type { CartPanelItem } from './useCartPanelItems'
import {
  getCartPanelExperienceTimesKey,
  isCartPanelItemCheckoutReady,
  prepareCartPanelExperienceCheckoutItem,
} from '../utils/cartPanelUtils'
import { refreshTripChatBedbankItemSessions } from '../utils/tripChatCheckoutBedbankUtils'
import { createAccommodationV2SessionsForCartItems } from 'lib/checkout/createAccommodationV2SessionsForCartItems'
import { refreshTripChatMultiCityFlights } from '../utils/tripChatCheckoutFlightUtils'
import { FlightViewTypes } from 'constants/flight'

interface UseTripChatCheckoutActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

interface UseTripChatCheckoutActionsReturn {
  checkoutCartItems: (items: Array<CartPanelItem>) => Promise<void>
  prepareCheckoutCartItems: (items: Array<CartPanelItem>) => void
  isCheckingOut: boolean
}

type CheckoutReadyItem = CartPanelItem & { cartItem: App.MultiCartItem }

interface FreshCheckoutReadyItems {
  inputKey: string
  activeItems: Array<CheckoutReadyItem>
}

interface PreparedCheckoutItems extends FreshCheckoutReadyItems {
  preparedAt: number
}

interface PendingPreparedCheckoutItems {
  inputKey: string
  promise: Promise<PreparedCheckoutItems | undefined>
}

const PREPARED_CHECKOUT_MAX_AGE_MS = 2 * 60 * 1000
const PREPARE_CART_REFETCH_FAILED = 'prepare_cart_refetch_failed'
const PREPARE_FLIGHT_REFRESH_FAILED = 'prepare_flight_refresh_failed'

function isCheckoutReadyItem(item: CartPanelItem): item is CheckoutReadyItem {
  return isCartPanelItemCheckoutReady(item) && !!item.cartItem
}

function expandCartPanelItems(items: Array<CartPanelItem>): Array<CartPanelItem> {
  return items.flatMap(item =>
    item.productType === 'flight' && item.multiCityLegs ? item.multiCityLegs : [item],
  )
}

function withFreshCartItemSnapshots(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  const freshItem = withFreshCartItemSnapshot(item, cartItems)
  if (freshItem.productType !== 'flight' || !freshItem.multiCityLegs) return freshItem

  return {
    ...freshItem,
    multiCityLegs: freshItem.multiCityLegs.map(leg => withFreshCartItemSnapshot(leg, cartItems) as typeof leg),
  }
}

function getCartPanelFlightCheckoutItem(item: CartPanelItem): App.Checkout.FlightItem | undefined {
  return item.productType === 'flight' && item.flightCartItem && isMultiCartFlightItem(item.flightCartItem) ?
    item.flightCartItem.item as App.Checkout.FlightItem :
    undefined
}

function getFlightItemKey(item: CartPanelItem): string {
  const flightItem = getCartPanelFlightCheckoutItem(item)
  return [
    flightItem?.searchId,
    flightItem?.expiry,
    flightItem?.flights.map(flight => flight.journeyId).join(','),
    flightItem?.flights.map(flight => flight.journeyKey).join(','),
  ].join(':')
}

function getCartPanelCheckoutInputKey(items: Array<CartPanelItem>): string {
  return expandCartPanelItems(items)
    .map(item => [
      item.key,
      item.productType,
      item.cartItemId,
      item.cartStatus,
      item.cartExcluded,
      getMultiCityGroupId(item),
      getMultiCityLegOrder(item),
      getFlightItemKey(item),
    ].join('|'))
    .join('||')
}

function isPreparedCheckoutUsable(
  prepared: PreparedCheckoutItems | undefined,
  inputKey: string,
): prepared is PreparedCheckoutItems {
  return !!prepared &&
    prepared.inputKey === inputKey &&
    Date.now() - prepared.preparedAt < PREPARED_CHECKOUT_MAX_AGE_MS
}

function hasGroupedMultiCityFlight(items: Array<CartPanelItem>): boolean {
  return items.some(item =>
    item.productType === 'flight' &&
    !!item.multiCityLegs &&
    item.multiCityLegs.length > 1,
  )
}

function withFreshCartItemSnapshot(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  if (!item.cartItemId) return item
  const cartItem = cartItems.get(item.cartItemId)
  if (item.productType === 'flight') {
    return {
      ...item,
      cartItem,
      flightCartItem: cartItem && isMultiCartFlightItem(cartItem) ? cartItem : undefined,
    }
  }

  return {
    ...item,
    cartItem,
  }
}

function toCheckoutItem(
  item: CartPanelItem & { cartItem: App.MultiCartItem },
  currency: string,
  experiences: App.State['experience']['experiences'],
  experienceTimes: App.State['experience']['experienceTimes'],
): App.Checkout.AnyItem {
  const checkoutItem = {
    ...item.cartItem.item,
    itemId: item.cartItem.item.itemId ?? item.cartItem.id,
    transactionKey: item.cartItem.item.transactionKey ?? uuidV4(),
  }
  if (item.productType !== 'experience' || !isExperienceItem(checkoutItem)) return checkoutItem

  const key = getCartPanelExperienceTimesKey(checkoutItem, currency)
  return prepareCartPanelExperienceCheckoutItem(
    checkoutItem,
    experiences[checkoutItem.experienceId],
    experienceTimes[checkoutItem.experienceId]?.[key]?.slots,
  )
}

function combineMultiCityCheckoutItems(
  activeItems: Array<CartPanelItem & { cartItem: App.MultiCartItem }>,
  checkoutItems: Array<App.Checkout.AnyItem>,
): Array<App.Checkout.AnyItem> {
  const groupIds = new Set<string>()
  activeItems.forEach(item => {
    const groupId = getMultiCityGroupId(item)
    if (groupId) groupIds.add(groupId)
  })
  if (groupIds.size === 0) return checkoutItems

  const consumedGroups = new Set<string>()
  const result: Array<App.Checkout.AnyItem> = []

  activeItems.forEach((item, index) => {
    const groupId = getMultiCityGroupId(item)
    if (!groupId) {
      result.push(checkoutItems[index])
      return
    }
    if (consumedGroups.has(groupId)) return
    consumedGroups.add(groupId)

    const group = activeItems
      .map((candidate, candidateIndex) => ({ item: candidate, checkoutItem: checkoutItems[candidateIndex] }))
      .filter(({ item }) => getMultiCityGroupId(item) === groupId)
      .sort((a, b) => getMultiCityLegOrder(a.item) - getMultiCityLegOrder(b.item))
    if (group.length <= 1) {
      result.push(checkoutItems[index])
      return
    }

    const legCheckoutItems = group.map(({ checkoutItem }) => checkoutItem).filter((checkoutItem): checkoutItem is App.Checkout.FlightItem => checkoutItem.itemType === 'flight')
    const flights = legCheckoutItems.flatMap(checkoutItem => checkoutItem.flights)
    const firstCheckoutItem = legCheckoutItems[0]
    const firstFlight = flights[0]
    const lastFlight = flights[flights.length - 1]
    // Require every leg to be a flight checkout item with at least one flight.
    // We intentionally do NOT require flights.length === group.length: a leg may
    // carry multiple flight segments (a connection), and the combined itinerary
    // should keep all of them in order.
    if (!firstCheckoutItem || !firstFlight || !lastFlight || legCheckoutItems.length !== group.length) {
      result.push(...group.map(({ checkoutItem }) => checkoutItem))
      return
    }

    result.push({
      ...firstCheckoutItem,
      fareType: 'multiCity',
      viewType: FlightViewTypes.MULTI_CITY,
      searchId: firstCheckoutItem.searchId,
      originAirportCode: firstFlight.departingAirportCode,
      destinationAirportCode: lastFlight.arrivalAirportCode,
      expiry: firstCheckoutItem.expiry,
      hasFinalFare: false,
      fareFetchError: undefined,
      // Drop the first leg's standalone total so the combined price is
      // re-derived from every leg's flights (otherwise the group undercharges
      // to leg one's fare until the final fare is fetched).
      totalFare: undefined,
      flights,
    })
  })

  return result
}

export function useTripChatCheckoutActions({
  chatId,
  multiCartId,
  refetchCartItems,
}: UseTripChatCheckoutActionsArgs): UseTripChatCheckoutActionsReturn {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'
  const shouldWaive = useShouldWaiveLuxPlusJoiningFee()
  const checkoutInFlightRef = useRef(false)
  const preparedCheckoutRef = useRef<PreparedCheckoutItems | undefined>(undefined)
  const pendingPreparedCheckoutRef = useRef<PendingPreparedCheckoutItems | undefined>(undefined)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const { subscriptionOffers, experiences, experienceTimes, email } = useAppSelector(state => ({
    subscriptionOffers: state.luxPlus.products.subscription.offers,
    experiences: state.experience.experiences,
    experienceTimes: state.experience.experienceTimes,
    email: state.auth?.account?.email,
  }), shallowEqual)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)
  const enableSabreContent = useFeatureFlag(OptimizelyFeatureFlags.flightsEnableSabreContent)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })

  const flightRefreshErrorMessage = intl.formatMessage({
    id: 'J1dfTHgb7DOLNSkugHPz',
    defaultMessage: 'This flight is no longer available. Please search for new flights.',
    description: 'Error shown when a multi-city flight group cannot be re-priced at checkout',
  })

  const charterBundleErrorMessage = intl.formatMessage({
    id: 'oS-wuNEBq6t8u5nGT-2z',
    defaultMessage: 'The included flight isn’t available for your selected dates. Please adjust your stay and try again.',
    description: 'Error shown when the bundled charter flight cannot be added at checkout',
  })

  const prepareFreshCheckoutItems = useCallback(async(items: Array<CartPanelItem>): Promise<PreparedCheckoutItems | undefined> => {
    const inputKey = getCartPanelCheckoutInputKey(items)
    if (!inputKey) return undefined

    const prepared = preparedCheckoutRef.current
    if (isPreparedCheckoutUsable(prepared, inputKey)) {
      return prepared
    }

    const pending = pendingPreparedCheckoutRef.current
    if (pending?.inputKey === inputKey) {
      return pending.promise
    }

    const promise = (async() => {
      let freshCartItems: Map<string, App.MultiCartItem>
      try {
        freshCartItems = await refetchCartItems()
      } catch {
        throw new Error(PREPARE_CART_REFETCH_FAILED)
      }

      const activeTopLevelItems = items
        .map(item => withFreshCartItemSnapshots(item, freshCartItems))
        .filter(isCartPanelItemCheckoutReady)
      let activeItems = expandCartPanelItems(activeTopLevelItems)
        .filter(isCheckoutReadyItem)
      if (activeItems.length === 0) return undefined

      try {
        activeItems = await refreshTripChatMultiCityFlights(activeItems, {
          currentCurrency: currency,
          currentRegionCode,
          email,
          enableSabreContent,
          requestLuxPlusPricing: canRequestLuxPlusFlightPricing,
        })
      } catch {
        throw new Error(PREPARE_FLIGHT_REFRESH_FAILED)
      }

      const nextPrepared: PreparedCheckoutItems = {
        inputKey,
        activeItems,
        preparedAt: Date.now(),
      }
      preparedCheckoutRef.current = nextPrepared
      return nextPrepared
    })()

    pendingPreparedCheckoutRef.current = { inputKey, promise }

    try {
      return await promise
    } finally {
      if (pendingPreparedCheckoutRef.current?.promise === promise) {
        pendingPreparedCheckoutRef.current = undefined
      }
    }
  }, [refetchCartItems, currency, currentRegionCode, canRequestLuxPlusFlightPricing, email, enableSabreContent])

  const prepareCheckoutCartItems = useCallback((items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId || !hasGroupedMultiCityFlight(items)) return
    const inputKey = getCartPanelCheckoutInputKey(items)
    if (!inputKey) return
    if (
      pendingPreparedCheckoutRef.current?.inputKey === inputKey ||
      isPreparedCheckoutUsable(preparedCheckoutRef.current, inputKey)
    ) {
      return
    }

    prepareFreshCheckoutItems(items).catch(() => {
      if (preparedCheckoutRef.current?.inputKey === inputKey) {
        preparedCheckoutRef.current = undefined
      }
    })
  }, [chatId, multiCartId, prepareFreshCheckoutItems])

  const checkoutCartItems = useCallback(async(items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId || checkoutInFlightRef.current) return
    checkoutInFlightRef.current = true
    setIsCheckingOut(true)

    try {
      let preparedItems: PreparedCheckoutItems | undefined
      try {
        preparedItems = await prepareFreshCheckoutItems(items)
      } catch(error) {
        showSnackbar(error instanceof Error && error.message === PREPARE_FLIGHT_REFRESH_FAILED ? flightRefreshErrorMessage : defaultErrorMessage, 'critical')
        return
      }
      const activeItems = preparedItems?.activeItems
      if (!activeItems || activeItems.length === 0) return

      getCartMachineActor()?.send({ type: 'set active cart', cartId: multiCartId })

      const checkoutSource: App.CheckoutSource = {
        type: 'trip_planner',
        multiCartId,
        externalReferenceId: String(chatId),
      }
      const perActiveItemCheckoutItems = activeItems.map(item => toCheckoutItem(item, currency, experiences, experienceTimes))
      const checkoutItems = combineMultiCityCheckoutItems(activeItems, perActiveItemCheckoutItems)

      // Charter flight bundles (Maldives MEL↔MLE direct): detect each charter
      // bundle from the live offer (shared helper; requireFlightsEnabled is false
      // at checkout because getOffersById doesn't reliably populate it) and derive
      // its charter (+ feeder) flight legs, then inject them so checkout opens with
      // the bundled flight. Batch the offer fetch so we don't fire one request per
      // hotel on the checkout-click path.
      const accommodationOfferIds = [...new Set(
        activeItems.flatMap(item =>
          item.productType === 'accommodation' && item.offerId ? [item.offerId] : [],
        ),
      )]
      const offersById = new Map<string, App.AnyOffer>()
      if (accommodationOfferIds.length > 0) {
        try {
          const offers = await getOffersById(accommodationOfferIds, currentRegionCode)
          for (const offer of offers) {
            if ('id' in offer) offersById.set(offer.id, offer)
          }
        } catch { /* per-item missing-offer guard below decides whether to block */ }
      }

      const charterBundleItems: Array<{
        offer: App.HotelOffer | App.HotelOfferSummary
        charterFlightBundle: CharterFlightBundleSelection
        occupancies: Array<App.Occupants>
        checkIn: string | undefined
        checkOut: string | undefined
        checkoutItem: App.Checkout.InstantBookingLEHotelItem
      }> = []
      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i]
        if (item.productType !== 'accommodation' || !item.offerId) continue
        const offer = offersById.get(item.offerId)
        // Missing offer: getOffersById dropped this one (it can return a subset),
        // so we can't inspect it to rule out a charter bundle. Fail closed only for
        // items that could actually carry a charter flight - a persisted selection,
        // or an instant-booking LE hotel (the only item type charter bundles ride
        // on). Unrelated accommodation (bedbank/SPP/villa, which getOffersById may
        // not even return) checks out normally instead of blocking the whole cart.
        if (!offer) {
          if (isMaldivesCharterEnabled && (item.charterFlightBundle || isInstantBookingLEHotelItem(perActiveItemCheckoutItems[i]))) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        const resolved = resolveCharterBundle({
          offer,
          isMaldivesCharterEnabled,
          persistedBundle: item.charterFlightBundle,
          occupancies: item.occupancies,
          requireFlightsEnabled: false,
        })
        if (!resolved) {
          if (item.charterFlightBundle) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        // The charter legs must ride on an instant-booking LE hotel item; if a
        // detected bundle isn't one we can't build its flight, so block checkout
        // rather than silently opening it flight-less.
        const checkoutItem = perActiveItemCheckoutItems[i]
        if (!isInstantBookingLEHotelItem(checkoutItem)) {
          showSnackbar(charterBundleErrorMessage, 'critical')
          return
        }
        charterBundleItems.push({
          offer: resolved.offer,
          charterFlightBundle: resolved.charterFlightBundle,
          occupancies: resolved.occupancies,
          checkIn: item.checkIn,
          checkOut: item.checkOut,
          checkoutItem,
        })
      }

      // Hydrate every charter bundle's flight legs in parallel (one search per hotel).
      let charterResults: Array<CharterBundleHydrationResult>
      try {
        charterResults = await Promise.all(charterBundleItems.map(({ offer, charterFlightBundle, occupancies, checkIn, checkOut, checkoutItem }) =>
          fetchCharterBundleFlightItems({
            offer,
            charterFlightBundle,
            accommodationItems: [checkoutItem],
            checkIn,
            checkOut,
            occupancies,
            region: currentRegionCode,
            currency,
            email,
          }),
        ))
      } catch(error) {
        reportLog('Trip chat charter bundle hydration threw', {
          error,
          chatId,
          multiCartId,
          offerIds: charterBundleItems.map(({ offer }) => offer.id),
        }, 'error')
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      const charterFlightItems: Array<App.Checkout.FlightItem> = []
      let hydrationFailed = false
      for (let i = 0; i < charterResults.length; i++) {
        const result = charterResults[i]
        if (!result.ok) {
          hydrationFailed = true
          const { offer, charterFlightBundle, checkIn, checkOut } = charterBundleItems[i]
          reportLog('Trip chat charter bundle hydration failed', {
            reason: result.reason,
            offerId: offer.id,
            charterBundleRoute: charterFlightBundle.charterBundleRoute,
            originAirportCode: charterFlightBundle.originAirportCode,
            fareClass: charterFlightBundle.fareClass,
            checkIn,
            checkOut,
            chatId,
            multiCartId,
          }, 'error')
          continue
        }
        charterFlightItems.push(...result.flightItems)
      }
      if (hydrationFailed) {
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      let transformed = transformUserCartItemsToCheckoutItems(checkoutItems, subscriptionOffers, shouldWaive)
      try {
        transformed = await refreshTripChatBedbankItemSessions(transformed, dispatch)
        transformed = await createAccommodationV2SessionsForCartItems(transformed, dispatch)
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      const allItems = [...transformed, ...charterFlightItems]
      dispatch(initialiseCheckout(allItems.map(item => ({ ...item, transactionKey: uuidV4() })), { checkoutSource }, true))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } finally {
      checkoutInFlightRef.current = false
      setIsCheckingOut(false)
    }
  }, [chatId, multiCartId, prepareFreshCheckoutItems, flightRefreshErrorMessage, charterBundleErrorMessage, defaultErrorMessage, isMaldivesCharterEnabled, experiences, experienceTimes, subscriptionOffers, shouldWaive, dispatch, currentRegionCode, currency, email])

  return { checkoutCartItems, prepareCheckoutCartItems, isCheckingOut }
}
